<template>
  <div class="products">
    <SDialog v-model:open="modalVisible">
      <SDialogContent
        :show-close-button="false"
        class="products orders-modal !fixed !inset-0 !top-0 !left-0 !w-screen !h-screen !max-w-none !rounded-none !translate-x-0 !translate-y-0 !p-0 !border-0 !gap-0 !grid-rows-[auto_auto_1fr] overflow-y-auto overflow-x-hidden [&>*]:min-w-0"
        @open-auto-focus.prevent
        @pointer-down-outside.prevent
        @focus-outside.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("button.products") }}
          </SDialogTitle>
        </SDialogHeader>
        <div class="customized-header d-flex flex-row fl-s-b">
          <div class="header-title d-flex fl-col fl-j-c">
            <div>
              <p>{{ $t("button.products") }}</p>
            </div>
          </div>
          <div
            class="close-button d-flex fl-col fl-j-c"
            data-cy="products-close-button"
            @click="close"
          >
            <SvgIcon
              :iconContent="closeIcon"
              :iconAlt="$t('button.close')"
              icon-style="width: 30px; height: 30px; filter: contrast(170%) invert(100%);"
            />
          </div>
        </div>
        <div class="customized-body">
          <div style="padding: 20px">
            <div class="body-wrapper">
              <div class="input-wrapper">
                <SInput
                  class="search-products-input h-10"
                  :placeholder="$t('products.placeholder')"
                  type="text"
                  :model-value="searchQuery"
                  @update:model-value="
                    (v) => {
                      searchQuery = String(v);
                      updateProducts();
                    }
                  "
                  data-cy="products-search-input"
                />
              </div>

              <ProductsTable :products="products" class="mt-4" />
            </div>

            <div class="date-picker" ref="datePickerContainer">
              <SPopover v-model:open="datePickerOpen">
                <SPopoverTrigger
                  class="h-0 w-0 opacity-0 pointer-events-none"
                />
                <SPopoverContent class="w-auto p-0" align="start">
                  <SCalendar
                    :model-value="selectedDateValue"
                    :is-date-unavailable="
                      (date) => isDateDisabled(date.toDate(getLocalTimeZone()))
                    "
                    initial-focus
                    @update:model-value="
                      (value) => {
                        if (value) {
                          selectDate(value.toDate(getLocalTimeZone()));
                        }
                        datePickerOpen = false;
                      }
                    "
                  />
                </SPopoverContent>
              </SPopover>
            </div>
          </div>
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  getCurrentInstance,
} from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import { ElNotification, NotificationHandle } from "element-plus";

import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { ModalStatus } from "@/constants/modal";
import ModalModule from "@/store/modules/modal";
import {
  IProductTableData,
  IProductCategory,
  ISmeetzPicture,
  PCTypes,
  RetailOptionType,
} from "@/types/db/product-category";
import { sumArray } from "@/utils/helpers/common";
import { createImageproxyUrl } from "@/utils/helpers/pictures";
import AppModule from "@/store/modules/app";
import {
  addToBasket,
  allowSharedStocksOverBooking,
  isProductAvailable,
  isSharedStocksWithOverbooking,
  selectPC,
} from "@/utils/vue-helpers/product-category";
import { sellOnSoldOut } from "@/utils/helpers/product-category";
import NotificationService from "@/services/notification.service";
import POSConfigModule from "@/store/modules/pos-config";
import closeIcon from "@/assets/images/svg/close.svg";
import { useOrderStore } from "@smeetz/order-store";
import { LineType } from "@smeetz/order-common";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { BookingStep } from "@/constants/booking";
import CashFloatModule from "@/store/modules/cash-float";
import { getPriceNonAutomaticAddons } from "@/utils/vue-helpers/product-slot-state";
import { hasAddonsEnabled } from "@/utils/vue-helpers/addons";
import { queryTimeslotsByProductCategories } from "@/dexie/timeslots";
import { searchProductCategories } from "@/dexie/product-category";

import ProductsTable from "./ProductsTable.vue";
import { provideProductsContext } from "./useProductsContext";

import {
  SCalendar,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
  SInput,
  SPopover,
  SPopoverContent,
  SPopoverTrigger,
} from "@smeetz/ui-core";
import { getLocalTimeZone, parseDate } from "@internationalized/date";

const { t } = useI18n({});
const emitter =
  getCurrentInstance()?.appContext.config.globalProperties.emitter;

const modalVisible = dialogVisibleForStatus(ModalStatus.Products);
const products = ref<IProductTableData[]>([]);
const searchQuery = ref("");
const selectedDate = ref<Date>(new Date());
// Stable ref so the picker doesn't reset its displayed month on every render (L3-7935).
const selectedDateValue = computed(() =>
  selectedDate.value
    ? parseDate(dayjs(selectedDate.value).format("YYYY-MM-DD"))
    : undefined
);
const datePickerOpen = ref(false);
const datePickerContainer = ref<HTMLDivElement | null>(null);
const selectedRecurringTicket = ref<IProductCategory | null>(null);

const clickPeriod = ref<number>(0);
const startTime = ref<number>(0);
const endTime = ref<number>(0);
const handleLongClickInterval = ref();
const isAddingToBasket = ref<boolean>(false);

const notification = ref<NotificationHandle | null>(null);
const notificationCounter = ref(1);
const notificationInterval = ref();

/**
 * User can sell when not in CompleteOrder step and a cash-float is open.
 *
 * @returns {boolean}
 */
const canSell = computed((): boolean => {
  return Boolean(
    useOrderMetaStore().step !== BookingStep.CompleteOrder &&
      CashFloatModule.cashFloat
  );
});

/**
 * Map of category uuid → quantity currently in the order. Used by the
 * Select-button cell to render the X{n} badge. Sums sale-line quantities of the
 * selected order grouped by category (order lines are keyed by category uuid,
 * matching `IProductCategory.uuid`).
 *
 * @returns {Record<string, number>}
 */
const orderQuantitiesByCategory = computed((): Record<string, number> => {
  const sumById: Record<string, number> = {};
  const orderLines = useOrderStore().selectedOrderSummary?.orderLines ?? [];

  for (const line of orderLines) {
    if (line.lineType !== LineType.SaleLine) {
      continue;
    }
    const id = line.categoryId;
    sumById[id] = (sumById[id] ?? 0) + line.quantity;
  }

  return sumById;
});

/**
 * Refresh the product list from Dexie based on the current search query.
 *
 * @returns {Promise<void>}
 */
async function updateProducts(): Promise<void> {
  const res = (await searchProductCategories(
    searchQuery.value
  )) as IProductTableData[];

  for (let i = 0; i < res.length; i++) {
    res[i] = await addProductData(res[i]);
  }

  products.value = res;
}

/**
 * Enrich a raw product with timeslot data (next future timeslot's availability
 * + start/end + timeslot id) for non-recurring tickets, and with retail
 * availability for retail rows.
 *
 * @param {IProductCategory} p - Raw product.
 * @returns {Promise<IProductTableData>}
 */
async function addProductData(p: IProductCategory): Promise<IProductTableData> {
  const product = p as IProductTableData;

  product.productCategoryId = product.id;

  if (product.type === PCTypes[PCTypes.Retail]) {
    product.dbAvailability = countAvailability(product);

    return product;
  }

  const now = new Date().toISOString();

  if (!product.isRecurring) {
    const timeslots = await queryTimeslotsByProductCategories({
      categoryIds: [product.id],
      endDateTimeAfter: now,
      limit: 1,
    });

    if (timeslots && timeslots.length) {
      product.dbAvailability = timeslots[0].availability;
      product.startDateTime = timeslots[0].startDateTime;
      product.endDateTime = timeslots[0].endDateTime;
      product.timeslot_ID = timeslots[0]._id;
    }
  }

  return product;
}

/**
 * Whether the row's Select button should be enabled. Stocks > 0 OR sell-on-
 * sold-out / shared-stocks-overbooking allowance.
 *
 * @param {IProductTableData} product - Row data.
 * @returns {boolean}
 */
function allowSelectProduct(product: IProductTableData): boolean {
  const condition =
    isProductAvailable(product) > 0 ||
    (isProductAvailable(product) <= 0 &&
      (sellOnSoldOut(product.prices, product.type) ||
        isSharedStocksWithOverbooking(product)));

  return Boolean(condition || allowSharedStocksOverBooking(product));
}

/**
 * Mouse/touch-down handler — starts the 1-second hold timer that fires
 * `handleLongClick`. Event param kept in the signature to match legacy
 * template usage; body doesn't read it (legacy didn't either, and lint
 * tolerated that — leave as-is).
 *
 * @param {Event} event - Pointer event.
 * @param {IProductTableData} product - Row data.
 * @returns {boolean} Sentinel.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function refreshPeriod(event: Event, product: IProductTableData): boolean {
  clickPeriod.value = 0;
  startTime.value = Date.now();
  handleLongClickInterval.value = setTimeout(
    () => handleLongClick(product),
    1000
  );

  return true;
}

/**
 * Long-click effect: open PCSelect modal for the held product.
 *
 * @param {IProductTableData} product - Row data.
 * @returns {boolean} Sentinel.
 */
function handleLongClick(product: IProductTableData): boolean {
  if (!allowSelectProduct(product)) {
    return true;
  }

  openPCSelectForProduct(product);

  return true;
}

/**
 * Open the quantity dialog for THIS row: the dialog resolves its category
 * and slot from these ids, so a concurrent gesture on another product can
 * no longer swap the sale context (L3-7629).
 *
 * @param {IProductTableData} product - Row data.
 */
function openPCSelectForProduct(product: IProductTableData): void {
  const { productCategoryId, timeslot_ID } = product;

  if (!productCategoryId || (!isRetail(product) && !timeslot_ID)) {
    return;
  }

  ModalModule.setModalProps({
    referrer: ModalStatus.Products,
    goBack: true,
    props: {
      productCategoryId: Number(productCategoryId),
      timeslotId: timeslot_ID,
    },
  });
  ModalModule.setStatus(ModalStatus.PCSelect);
}

/**
 * Short-click / mouseup handler — finalizes selection. For recurring tickets
 * just stores the row; for others, runs the basket flow.
 *
 * @param {Event | null} event - Pointer event (null on recurring path).
 * @param {IProductCategory} p - Raw product (cast to IProductTableData inside).
 * @returns {Promise<boolean>} Sentinel.
 */
async function selectProduct(
  event: Event | null,
  p: IProductCategory
): Promise<boolean> {
  if (event) {
    event.preventDefault();
  }

  clearInterval(handleLongClickInterval.value);
  endTime.value = Date.now();
  clickPeriod.value = endTime.value - startTime.value;

  const product = p as IProductTableData;

  if (product.isRecurring) {
    selectedRecurringTicket.value = product;

    return true;
  }

  if (clickPeriod.value < 1000 && allowSelectProduct(product)) {
    // Ignore taps while a previous tap is still adding its line: two
    // concurrent upserts both read the pre-create state, both route to
    // create, and the second is rejected as a duplicate line. The ignored
    // tap is a no-op; the next tap updates the quantity.
    if (isAddingToBasket.value) {
      return true;
    }
    isAddingToBasket.value = true;
    try {
      const isRetail = product.type === PCTypes[PCTypes.Retail];
      const productCategorySelectRes = await selectPC(product, isRetail);

      if (!productCategorySelectRes) {
        // selectPC already surfaced the error for unresolvable rows
        return true;
      }

      const { pssQuantity, pc, timeslot } = productCategorySelectRes;

      if (pssQuantity) {
        const price = product.prices?.length ? product.prices[0] : null;
        const pssAddons = await getPriceNonAutomaticAddons(price);

        if (pssAddons.length && hasAddonsEnabled.value) {
          openPCSelectForProduct(product);
        } else {
          try {
            await addToBasket({
              pssQuantity,
              isRetail,
              productCategory: pc,
              timeslot,
            });
          } catch {
            // journaled + surfaced by the order helpers; nothing is added
            return false;
          }
          handleNotification();
        }
      } else {
        openPCSelectForProduct(product);
      }

      return true;
    } finally {
      isAddingToBasket.value = false;
    }
  }

  NotificationService.showCustomError(t("products.tickets-unavailable"));

  return true;
}

/**
 * Show / refresh / rate-limit the success notification after adding to basket.
 *
 * @returns {boolean} Sentinel.
 */
function handleNotification(): boolean {
  if (notification.value) {
    clearInterval(notificationInterval.value);
    notificationCounter.value += 1;
    notification.value.close();
    notification.value = ElNotification({
      title: `${t("common.success")} X${notificationCounter.value}`,
      message: t("products.tickets-added"),
      type: "success",
      duration: 0,
    });
  } else {
    notification.value = ElNotification({
      title: t("common.success"),
      message: t("products.tickets-added"),
      type: "success",
      duration: 0,
    });
    notificationCounter.value = 1;
  }

  notificationInterval.value = setTimeout(() => {
    notification.value?.close();
    notification.value = null;
  }, 4500);

  return true;
}

/**
 * @param {IProductCategory} product - Row data.
 * @returns {boolean}
 */
function isRetail(product: IProductCategory): boolean {
  return product.type === PCTypes[PCTypes.Retail];
}

/**
 * Read a translated value from a `xxxLocale` object using the active app language.
 *
 * @param {Record<string, string>} localeObject - Object keyed by language tag.
 * @returns {string}
 */
function getTranslationForProducts(
  localeObject: Record<string, string>
): string {
  const languageTag = AppModule.language;

  return localeObject[languageTag];
}

/**
 * Retail-stock arithmetic.
 *
 * @param {IProductCategory} product - Row data.
 * @returns {number}
 */
function countAvailability(product: IProductCategory): number {
  if (product.type !== PCTypes[PCTypes.Retail]) {
    return 0;
  }

  if (product.prices.length === 1) {
    return product.prices[0].quantity;
  }

  return sumArray(
    product.prices
      .filter((pr) =>
        pr.retail.every(
          (re) => re.type !== RetailOptionType.Default_Retail_Option
        )
      )
      .map((pr) => pr.quantity)
  );
}

/**
 * Default RetailOption SmeetzPicture (for picture rendering).
 *
 * @param {IProductCategory} pc - Product category.
 * @returns {ISmeetzPicture | undefined}
 */
function getDefaultRetailOptionSmeetzPicture(
  pc: IProductCategory
): ISmeetzPicture | undefined {
  if (pc.prices.length === 1) {
    return pc.prices[0].retail[0].smeetzPictures[0];
  }

  return pc.prices.find((pr) =>
    pr.retail.some((rt) => rt.type === RetailOptionType.Default_Retail_Option)
  )?.retail[0].smeetzPictures[0];
}

/**
 * Resolved picture URL for a row.
 *
 * @param {IProductCategory} pc - Product category.
 * @returns {string}
 */
function getPictureUrl(pc: IProductCategory): string {
  if (pc.type !== PCTypes[PCTypes.Retail]) {
    return pc.product.pictureUrl;
  }

  return createImageproxyUrl(getDefaultRetailOptionSmeetzPicture(pc), {
    width: 150,
    height: 150,
  });
}

/**
 * Close the modal.
 *
 * @returns {boolean} Sentinel.
 */
function close(): boolean {
  ModalModule.setStatus(ModalStatus.Hidden);

  return true;
}

/**
 * Date-picker change handler. For recurring tickets, scrolls the calendar to
 * the first available timeslot on the chosen date.
 *
 * @param {Date} date - Picked date.
 * @returns {boolean} Sentinel.
 */
function selectDate(date: Date): boolean {
  selectedDate.value = date;

  if (!selectedRecurringTicket.value) {
    return true;
  }

  const product = selectedRecurringTicket.value;
  const configGrids = POSConfigModule.configGrids;
  const gridtoSelect = configGrids.find((grid) => {
    return product.ticketGroups.includes(grid.ticketGroupId);
  });

  if (gridtoSelect) {
    POSConfigModule.selectGrid(gridtoSelect);
    emitter?.emit("scrollToRecurringTicket", { date, product });
    emitter?.on("calendarCardsMounted", async () => {
      await nextTick();
      emitter?.emit("scrollToRecurringTicket", { date, product });
    });
  }

  return true;
}

/**
 * Disable past dates in the date picker.
 *
 * @param {Date} date - Date being tested.
 * @returns {boolean}
 */
function isDateDisabled(date: Date): boolean {
  const dateJS = dayjs(date);

  if (dateJS.isBefore(dayjs(), "day")) {
    return true;
  }

  return false;
}

/**
 * Position the (off-screen) date-picker next to the clicked Select button and
 * focus it. Used for recurring-ticket selection.
 *
 * @param {number} id - Product id.
 * @returns {boolean} Sentinel.
 */
function openCalendar(id: number): boolean {
  const button = document.getElementById(`selectButton-${id}`);

  if (!button) {
    return true;
  }

  if (!datePickerContainer.value) {
    return true;
  }

  const buttonRect = button.getBoundingClientRect();

  datePickerContainer.value.style.position = "absolute";
  datePickerContainer.value.style.left = `${buttonRect.right - 80}px`;
  datePickerContainer.value.style.top = `${buttonRect.top}px`;
  datePickerOpen.value = true;

  return true;
}

provideProductsContext({
  refreshPeriod,
  selectProduct,
  openCalendar,
  allowSelectProduct,
  canSell,
  orderQuantitiesByCategory,
  getTranslationForProducts,
  getPictureUrl,
});

onMounted(() => {
  updateProducts();
});

onBeforeUnmount(() => {
  emitter?.off("calendarCardsMounted");
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

.products {
  .customized-header {
    padding-left: 30px;
    padding-right: 30px;
    background-color: #004c53;
    height: 45px;
    color: white;

    .header-title p {
      font-size: 18px;
      font-weight: 700;
    }

    .go-back-icon {
      margin-right: 15px;
    }
  }
  .search-products-input :focus {
    border-color: rgba(212, 215, 217, 1);
  }
  .search-products-input {
    width: 50%;
  }

  .input-wrapper {
    display: flex;
    justify-content: center;
  }

  .grid-content {
    padding: 0 0 0 10px;
    border-radius: 4px;
    min-height: 12vh;
    background: #e9e9eb;
    margin-bottom: 15px;
    color: $color-text-primary;
    .clamped {
      -webkit-line-clamp: 4;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .product-title {
    font-weight: bold;
    word-break: break-word;
  }

  .word-alignment {
    word-break: normal;
  }

  img {
    width: 98px;
    height: 98px;
    border-radius: 6px;
    object-fit: cover;
  }

  .select-product-btn {
    min-height: 40px;
    min-width: 100%;
    border: 1px solid rgb(217, 223, 229);
    padding: 8px;
    border-radius: 4px;
    background-color: rgb(253, 254, 254);
    cursor: pointer;
  }
  .break {
    height: 0;
    flex-grow: 3;
  }

  .cursor-pointer {
    cursor: pointer;
  }
  .date-picker {
    overflow: hidden;
    width: 0;
    height: 0;
    opacity: 0;
  }
  .order-quantity {
    margin-left: 5px;
  }
  .tooltip {
    position: relative;
  }

  .tooltip .tooltiptext {
    visibility: hidden;
    background-color: black;
    color: #fff;
    text-align: center;
    padding: 10px;
    bottom: 100%;
    left: 50%;
    margin-left: -50%;
    margin-bottom: 25px;
    border-radius: 6px;
    position: absolute;
    z-index: 1;
  }

  .tooltip:hover .tooltiptext {
    visibility: visible;
  }
}
</style>
